import React, { useContext, useMemo, useState } from 'react';

import { Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassSessionsToolbar from './ClassSessionsToolbar';
import ClassSessionTableRow from './ClassSessionTableRow';
import { PaginationContainer, TablePagination, useTablePagination } from '../../../../../components/TablePagination';
import { type DashboardClassSessionRecord } from '../../../../../components/templates/types';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassContext } from '../../../ClassContext';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const ClassDetailClassSessions = () => {
    const { t } = useTranslation('portal');
    const { classData } = useContext(ClassContext);
    const { authMode } = useContainer(AdminAuthContainer);
    console.log('ClassDetailClassSessions classData', classData, authMode);

    const [searchText, setSearchText] = useState('');

    const filteredClassSessions = useMemo(
        () =>
            classData.class_sessions?.filter((classSession) => {
                const includedInDate =
                    classSession.date_str.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) ||
                    Moment(new Date(Number(classSession.start_time) * 1000))
                        .format('MMMM Do YYYY')
                        .toLocaleLowerCase()
                        .includes(searchText.toLocaleLowerCase());
                const includedInTopic = classSession.class_session_topic
                    ?.toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());

                return includedInDate || includedInTopic;
            }),
        [searchText, classData.class_sessions]
    );

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredClassSessions);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
        setPage(1);
    };

    if (classData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <ClassSessionsToolbar searchText={searchText} handleChangeSearch={handleChangeSearch} />
            <StyledTable>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('date')}</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{t('user-count')}</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{t('talk-time')}</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{t('chats')}</TableCell>
                        <TableCell>{t('tools')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedValues.map((classSession, index) => (
                        <ClassSessionTableRow
                            classSession={classSession as DashboardClassSessionRecord}
                            key={index}
                        ></ClassSessionTableRow>
                    ))}
                </TableBody>
            </StyledTable>
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>
        </>
    );
};
